import { Button, KvixIcon, Text } from "@kvix/ui";
import {
  Box,
  CardActions,
  CardContent,
  CircularProgress,
  Collapse,
  Dialog,
  Paper,
  useTheme,
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import * as Sentry from "@sentry/react";
import { CardElement, Elements, useElements } from "@stripe/react-stripe-js";
import {
  SetupIntent,
  StripeCardElementChangeEvent,
  StripeError,
} from "@stripe/stripe-js";
import React, { FC, Fragment, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAsync } from "react-use";
import styled from "styled-components";
import { PaymentContext } from "../../../context";

const CardElementContainer = styled.div<{ dark: boolean }>`
  .StripeElement {
    filter: ${(props) =>
      props.dark ? "invert(90%) hue-rotate(180deg)" : null};

    box-sizing: border-box;
    height: 44px;
    padding: 12px 12px;

    border: 1px solid transparent;
    border-radius: 4px;
    background-color: #f3f3f3;

    transition: background 150ms ease;

    &--focus {
      background-color: ${(props) => (props.dark ? "white" : "#e6e6e6")};
    }

    &--invalid {
      border-color: #fa755a;
    }

    &--webkit-autofill {
      background-color: white !important;
    }
  }
`;

const CreditCardField: FC = () => {
  const theme = useTheme();

  return (
    <CardElementContainer dark={theme.palette.type === "dark"}>
      <CardElement options={{ hidePostalCode: true, iconStyle: "solid" }} />
    </CardElementContainer>
  );
};

const useCardElementState = () => {
  const elements = useElements();
  const [state, setState] = useState<StripeCardElementChangeEvent>(null);

  useEffect(() => {
    const element = elements.getElement(CardElement);

    if (element) {
      element.on("change", setState);
    }
  }, [elements]);

  return state;
};

interface SubmitButtonProps {
  intent: SetupIntent;
  onLoading: () => void;
  onSuccess: (setupIntent: SetupIntent) => void;
  onError: (error: StripeError) => void;
}

const SubmitButton: FC<SubmitButtonProps> = (props) => {
  const { stripe, actions } = useContext(PaymentContext);
  const elements = useElements();
  const cardState = useCardElementState();
  const [loading, setLoading] = useState(false);
  const { t, ready } = useTranslation("general");

  const save = async () => {
    const cardElement = elements.getElement(CardElement);

    props.onLoading();

    setLoading(true);

    try {
      const response = await stripe.confirmCardSetup(
        props.intent.client_secret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      await actions.fetchSavedCards();

      if (response.error) {
        props.onError(response.error);
      } else {
        props.onSuccess(response.setupIntent);
      }
    } catch (error) {
      Sentry.captureException(error, { tags: { package: "client" } });
      console.log(error);
    }

    setLoading(false);
  };

  if (!ready) {
    return null;
  }

  return (
    <Button
      onClick={save}
      disabled={loading || !cardState?.complete}
      loading={loading}
    >
      {t("buttons.confirm")}
    </Button>
  );
};

const CardErrors: FC = (props) => {
  const cardState = useCardElementState();

  let error = cardState?.error;

  if (!error) {
    return null;
  }

  return (
    <Text color="error" variant="body2">
      <Box
        component="span"
        display="grid"
        gridTemplateColumns="auto 1fr"
        gridGap={8}
      >
        <KvixIcon.Error />

        <Box component="span" mt="1px" fontWeight={500}>
          {error.message}
        </Box>
      </Box>
    </Text>
  );
};

const Modal = styled(Paper)`
  &.MuiDialog-paper {
    width: 100%;
    max-width: 450px;
  }
`;

const ModalContent = styled(CardContent)`
  &.MuiCardContent-root {
    padding: 24px;
  }
`;

const ModalActions = styled(CardActions)`
  &.MuiCardActions-root {
    padding: 24px;
    justify-content: flex-end;
  }
`;

interface Props {
  open: boolean;
  onClose: () => void;
}

const Form: FC<Props> = (props) => {
  const { actions } = useContext(PaymentContext);
  const [submitError, setSubmitError] = useState<StripeError>(null);
  const [showSubmitError, setShowSubmitError] = useState(false);
  const intent = useAsync(actions.createSetupIntent);
  const { t, ready } = useTranslation(["account", "general"]);

  const cancel = () => {
    if (intent.value) {
      actions.cancelSetupIntent(intent.value.id);
    }

    props.onClose();
  };

  if (!ready) {
    return null;
  }

  if (intent.loading) {
    return (
      <Box py={12} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (intent.error) {
    return <Text variant="caption">Error!</Text>;
  }

  return (
    <Fragment>
      <ModalContent>
        <Text gutterBottom variant="h4">
          {t("account.paymentMethod.addCardModal.title")}
        </Text>

        <Text variant="body2">
          {t("account.paymentMethod.addCardModal.subtitle")}
        </Text>

        <Box mt={3} mb={1}>
          <CreditCardField />
        </Box>

        <CardErrors />

        {submitError && (
          <Collapse in={showSubmitError} mountOnEnter unmountOnExit>
            <Box pt={2}>
              <Alert severity="error" variant="filled">
                <AlertTitle>
                  {t("account.paymentMethod.addCardModal.addCardError")}
                </AlertTitle>
                {submitError.message}
              </Alert>
            </Box>
          </Collapse>
        )}
      </ModalContent>

      <ModalActions>
        <Button variant="text" onClick={cancel}>
          {t("general:buttons.cancel")}
        </Button>

        <Box ml={2}>
          <SubmitButton
            intent={intent.value}
            onLoading={() => {
              setShowSubmitError(false);
            }}
            onSuccess={() => {
              setSubmitError(null);
              props.onClose();
            }}
            onError={(error) => {
              setSubmitError(error);
              setShowSubmitError(true);
            }}
          />
        </Box>
      </ModalActions>
    </Fragment>
  );
};

export const CreditCardFormDialog: FC<Props> = (props) => {
  const { stripe } = useContext(PaymentContext);

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      PaperComponent={Modal}
      disableBackdropClick
    >
      <Elements stripe={stripe}>
        <Form {...props} />
      </Elements>
    </Dialog>
  );
};
